// LocationPicker Component
// Google Maps location picker with autocomplete

import React, { useState, useRef } from 'react';
import { GoogleMap, Marker, StandaloneSearchBox, useLoadScript } from '@react-google-maps/api';
import { FaMapMarkerAlt, FaCrosshairs } from 'react-icons/fa';
import styles from './LocationPicker.module.css';

const libraries = ['places'];

const LocationPicker = ({ value, onLocationSelect }) => {
  const [searchBox, setSearchBox] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 39.8283, lng: -98.5795 }); // Default: USA center
  const [mapZoom, setMapZoom] = useState(4);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [gettingUserLocation, setGettingUserLocation] = useState(false);
  const mapRef = useRef(null);

  // Load Google Maps
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  // Initialize with existing location
  React.useEffect(() => {
    if (value?.coordinates?.coordinates?.[0] && value?.coordinates?.coordinates?.[1]) {
      const [lng, lat] = value.coordinates.coordinates;
      setSelectedPosition({ lat, lng });
      setMapCenter({ lat, lng });
      setMapZoom(12);
    }
  }, []);

  // Handle search box load
  const onSearchBoxLoad = (ref) => {
    setSearchBox(ref);
  };

  // Handle place selection from search
  const onPlacesChanged = () => {
    if (!searchBox) return;
    
    const places = searchBox.getPlaces();
    
    if (places && places.length > 0) {
      const place = places[0];
      const location = place.geometry.location;
      const lat = location.lat();
      const lng = location.lng();

      setSelectedPosition({ lat, lng });
      setMapCenter({ lat, lng });
      setMapZoom(16);

      // Parse address components
      let streetNumber = '';
      let route = '';
      let city = '';
      let state = '';
      let country = '';
      let zipCode = '';

      if (place.address_components) {
        place.address_components.forEach((component) => {
          if (component.types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          if (component.types.includes('route')) {
            route = component.long_name;
          }
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
          if (component.types.includes('sublocality_level_1') && !city) {
            city = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name;
          }
          if (component.types.includes('country')) {
            country = component.long_name;
          }
          if (component.types.includes('postal_code')) {
            zipCode = component.long_name;
          }
        });
      }

      // Combine street number and route for full street address
      const street = [streetNumber, route].filter(Boolean).join(' ');

      // Call parent onChange with parsed location
      const locationData = {
        street: street,
        city: city,
        state: state,
        country: country,
        zipCode: zipCode,
        coordinates: {
          type: 'Point',
          coordinates: [lng, lat]
        }
      };

      if (onLocationSelect) {
        onLocationSelect(locationData);
      }
    }
  };

  // Handle map click
  const handleMapClick = async (e) => {
    if (!e || !e.latLng) return;
    
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setSelectedPosition({ lat, lng });

    // Reverse geocode to get address
    try {
      if (!window.google || !window.google.maps) {
        throw new Error('Google Maps not loaded');
      }
      
      const geocoder = new window.google.maps.Geocoder();
      const result = await geocoder.geocode({ location: { lat, lng } });

      if (result.results && result.results.length > 0) {
        const place = result.results[0];
        
        let streetNumber = '';
        let route = '';
        let city = '';
        let state = '';
        let country = '';
        let zipCode = '';

        place.address_components.forEach((component) => {
          if (component.types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          if (component.types.includes('route')) {
            route = component.long_name;
          }
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
          if (component.types.includes('sublocality_level_1') && !city) {
            city = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name;
          }
          if (component.types.includes('country')) {
            country = component.long_name;
          }
          if (component.types.includes('postal_code')) {
            zipCode = component.long_name;
          }
        });

        // Combine street number and route for full street address
        const street = [streetNumber, route].filter(Boolean).join(' ');

        const locationData = {
          street: street,
          city: city,
          state: state,
          country: country,
          zipCode: zipCode,
          coordinates: {
            type: 'Point',
            coordinates: [lng, lat]
          }
        };

        if (onLocationSelect) {
          onLocationSelect(locationData);
        }
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
    }
  };

  // Get user's current location
  const handleGetUserLocation = () => {
    setGettingUserLocation(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          setMapCenter({ lat, lng });
          setMapZoom(12);
          setSelectedPosition({ lat, lng });

          // Reverse geocode to get address
          try {
            if (!window.google || !window.google.maps) {
              console.error('Google Maps not loaded');
              setGettingUserLocation(false);
              return;
            }
            
            const geocoder = new window.google.maps.Geocoder();
            const result = await geocoder.geocode({ location: { lat, lng } });

            if (result.results && result.results.length > 0) {
              const place = result.results[0];
              
              let streetNumber = '';
              let route = '';
              let city = '';
              let state = '';
              let country = '';
              let zipCode = '';

              place.address_components.forEach((component) => {
                if (component.types.includes('street_number')) {
                  streetNumber = component.long_name;
                }
                if (component.types.includes('route')) {
                  route = component.long_name;
                }
                if (component.types.includes('locality')) {
                  city = component.long_name;
                }
                if (component.types.includes('sublocality_level_1') && !city) {
                  city = component.long_name;
                }
                if (component.types.includes('administrative_area_level_1')) {
                  state = component.long_name;
                }
                if (component.types.includes('country')) {
                  country = component.long_name;
                }
                if (component.types.includes('postal_code')) {
                  zipCode = component.long_name;
                }
              });

              // Combine street number and route for full street address
              const street = [streetNumber, route].filter(Boolean).join(' ');

              const locationData = {
                street: street,
                city: city,
                state: state,
                country: country,
                zipCode: zipCode,
                coordinates: {
                  type: 'Point',
                  coordinates: [lng, lat]
                }
              };

              if (onLocationSelect) {
                onLocationSelect(locationData);
              }
            }
          } catch (error) {
            console.error('Error reverse geocoding:', error);
          }
          
          setGettingUserLocation(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setGettingUserLocation(false);
        }
      );
    } else {
      setGettingUserLocation(false);
      alert('Geolocation is not supported by your browser');
    }
  };

  if (loadError) {
    return (
      <div className={styles.error}>
        <p>Error loading Google Maps</p>
        <p className={styles.errorDetail}>
          Please check that REACT_APP_GOOGLE_MAPS_API_KEY is set in your environment variables.
        </p>
      </div>
    );
  }

  if (!isLoaded) {
    return <div className={styles.loading}>Loading maps...</div>;
  }
  
  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return (
      <div className={styles.error}>
        <p>Google Maps API key not configured</p>
        <p className={styles.errorDetail}>
          Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file
        </p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Search Box */}
      <div className={styles.searchBoxContainer}>
        <div className={styles.searchInstructions}>
          <FaMapMarkerAlt />
          <span><strong>Search for a location</strong> or click on the map</span>
        </div>
        <StandaloneSearchBox
          onLoad={onSearchBoxLoad}
          onPlacesChanged={onPlacesChanged}
        >
          <input
            type="text"
            placeholder="🔍 Search: San Diego, CA"
            className={styles.searchInput}
          />
        </StandaloneSearchBox>
        
        <button
          type="button"
          className={styles.myLocationButton}
          onClick={handleGetUserLocation}
          disabled={gettingUserLocation}
        >
          <FaCrosshairs />
          {gettingUserLocation ? 'Getting location...' : 'Use My Location'}
        </button>
      </div>

      {/* Selected Location Display */}
      {selectedPosition && value && (
        <div className={styles.selectedLocation}>
          <FaMapMarkerAlt className={styles.locationIcon} />
          <div className={styles.locationDetails}>
            <div className={styles.locationText}>
              {[value.city, value.state, value.country].filter(Boolean).join(', ')}
            </div>
            <div className={styles.coordinates}>
              {selectedPosition.lat.toFixed(6)}, {selectedPosition.lng.toFixed(6)}
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      <GoogleMap
        mapContainerClassName={styles.map}
        center={mapCenter}
        zoom={mapZoom}
        onClick={handleMapClick}
        onLoad={(map) => { mapRef.current = map; }}
        options={{
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: false,
          zoomControl: true
        }}
      >
        {selectedPosition && (
          <Marker
            position={selectedPosition}
            animation={window.google?.maps?.Animation?.DROP}
          />
        )}
      </GoogleMap>
    </div>
  );
};

export default LocationPicker;

